import { useState, useEffect, useMemo } from 'react';
import { notificationService } from '../../../src/services/notification.service';
import { usePermission } from '../../../src/utils/permissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import {
  Building2,
  Calendar,
  Plus,
  Search,
  MoreVertical,
  Eye,
  Edit,
  CheckCircle,
  Archive,
  X,
  User,
  Save,
  SlidersHorizontal,
  AlertTriangle
} from 'lucide-react';
import { projectService } from '../../../src/services/project.service';
import { authService } from '../../../src/services/auth.service';
import { userService } from '../../../src/services/user.service';
import { httpClient } from '../../../src/services/http.client';
import { useAuth } from '../../../src/hooks/useAuth';
import { useOrgCurrency } from '../../../src/hooks/useOrgCurrency';
import { formatCurrency, formatCurrencyCompact } from '../../../src/utils/currency';
import { useBoqs } from '../../../src/hooks/queries/use-boqs';
import { useOrgUsers } from '../../../src/hooks/queries/use-org-users';
import { useProjects } from '../../../src/hooks/queries/use-projects';
import { useAppNav } from '../../../src/contexts/app-nav.context';

const COUNTRY_NAMES: Record<string, string> = {
  AF:'Afghanistan',AL:'Albania',DZ:'Algeria',AO:'Angola',AR:'Argentina',AM:'Armenia',
  AU:'Australia',AT:'Austria',AZ:'Azerbaijan',BH:'Bahrain',BD:'Bangladesh',BY:'Belarus',
  BE:'Belgium',BZ:'Belize',BJ:'Benin',BO:'Bolivia',BA:'Bosnia and Herzegovina',BW:'Botswana',
  BR:'Brazil',BN:'Brunei',BG:'Bulgaria',BF:'Burkina Faso',BI:'Burundi',KH:'Cambodia',
  CM:'Cameroon',CA:'Canada',CF:'Central African Republic',TD:'Chad',CL:'Chile',CN:'China',
  CO:'Colombia',CG:'Congo',CR:'Costa Rica',HR:'Croatia',CU:'Cuba',CY:'Cyprus',CZ:'Czech Republic',
  DK:'Denmark',DO:'Dominican Republic',EC:'Ecuador',EG:'Egypt',SV:'El Salvador',EE:'Estonia',
  ET:'Ethiopia',FI:'Finland',FR:'France',GA:'Gabon',GE:'Georgia',DE:'Germany',GH:'Ghana',
  GR:'Greece',GT:'Guatemala',GN:'Guinea',HN:'Honduras',HU:'Hungary',IN:'India',ID:'Indonesia',
  IR:'Iran',IQ:'Iraq',IE:'Ireland',IL:'Israel',IT:'Italy',JM:'Jamaica',JP:'Japan',JO:'Jordan',
  KZ:'Kazakhstan',KE:'Kenya',KW:'Kuwait',KG:'Kyrgyzstan',LV:'Latvia',LB:'Lebanon',LY:'Libya',
  LT:'Lithuania',LU:'Luxembourg',MK:'North Macedonia',MG:'Madagascar',MW:'Malawi',MY:'Malaysia',
  ML:'Mali',MT:'Malta',MR:'Mauritania',MX:'Mexico',MD:'Moldova',MN:'Mongolia',MA:'Morocco',
  MZ:'Mozambique',MM:'Myanmar',NA:'Namibia',NP:'Nepal',NL:'Netherlands',NZ:'New Zealand',
  NI:'Nicaragua',NE:'Niger',NG:'Nigeria',NO:'Norway',OM:'Oman',PK:'Pakistan',PA:'Panama',
  PY:'Paraguay',PE:'Peru',PH:'Philippines',PL:'Poland',PT:'Portugal',QA:'Qatar',RO:'Romania',
  RU:'Russia',RW:'Rwanda',SA:'Saudi Arabia',SN:'Senegal',RS:'Serbia',SL:'Sierra Leone',
  SG:'Singapore',SK:'Slovakia',SI:'Slovenia',SO:'Somalia',ZA:'South Africa',KR:'South Korea',
  SS:'South Sudan',ES:'Spain',LK:'Sri Lanka',SD:'Sudan',SE:'Sweden',CH:'Switzerland',
  SY:'Syria',TW:'Taiwan',TJ:'Tajikistan',TZ:'Tanzania',TH:'Thailand',TG:'Togo',
  TT:'Trinidad and Tobago',TN:'Tunisia',TR:'Turkey',TM:'Turkmenistan',UG:'Uganda',UA:'Ukraine',
  AE:'United Arab Emirates',GB:'United Kingdom',US:'United States',UY:'Uruguay',UZ:'Uzbekistan',
  VE:'Venezuela',VN:'Vietnam',YE:'Yemen',ZM:'Zambia',ZW:'Zimbabwe',
};

// Advanced Filter Component
function AdvancedFilterPopover() {
  const [filters, setFilters] = useState({
    status: '',
    manager: '',
    category: '',
    budgetRange: '',
    dateRange: ''
  });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline">
          <SlidersHorizontal className="h-4 w-4 mr-2" />
          Advanced Filters
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium">Filter Projects</h4>
            <p className="text-sm text-muted-foreground">Refine your project view</p>
          </div>

          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="filter-status">Status</Label>
              <Select value={filters.status} onValueChange={(value) => setFilters({...filters, status: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="planning">Planning</SelectItem>
                  <SelectItem value="delayed">Delayed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="on-hold">On Hold</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="filter-manager">Project Manager</Label>
              <Select value={filters.manager} onValueChange={(value) => setFilters({...filters, manager: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="All managers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Managers</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button className="w-full">Apply Filters</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// New Project Modal
function NewProjectModal({ onProjectCreated }: { onProjectCreated?: () => void }) {
  const [formData, setFormData] = useState({
    name: '',
    projectNumber: '',
    type: '',
    priority: 'medium',
    description: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    startDate: '',
    endDate: '',
    totalBudget: '',
    linkedBOQ: '',
    manager: '',
    currency: 'USD'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const { orgId } = useAuth();
  const { data: organizationUsers = [] } = useOrgUsers(open ? orgId : null);
  const { data: boqList = [] } = useBoqs(open ? orgId : null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate required fields
      if (!formData.name.trim()) {
        console.error('Project name is required');
        return;
      }
      if (!formData.type) {
        console.error('Project type is required');
        return;
      }
      if (!formData.linkedBOQ) {
        console.error('Linked BOQ is required');
        return;
      }

      if (!orgId) {
        console.error('User organization not found. Please contact administrator.');
        return;
      }

      // Map to backend schema format
      const projectData = {
        project_name: formData.name,
        project_number: formData.projectNumber || null,
        project_type: formData.type.toLowerCase(),
        status: 'planning',
        priority: formData.priority.toLowerCase(),
        description: formData.description || null,
        address: formData.address || null,
        city: formData.city || null,
        state: formData.state || null,
        zip_code: formData.zipCode || null,
        country: formData.country || 'US',
        project_manager_id: formData.manager || null,
        client_name: formData.clientName || null,
        client_email: formData.clientEmail || null,
        client_phone: formData.clientPhone || null,
        start_date: formData.startDate ? new Date(formData.startDate).toISOString().split('T')[0] : null,
        end_date: formData.endDate ? new Date(formData.endDate).toISOString().split('T')[0] : null,
        total_budget: formData.totalBudget ? parseFloat(formData.totalBudget) : null,
        linked_boq_id: formData.linkedBOQ || null,
        organization_id: orgId,
      };

      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.log('Project Creation Payload:', JSON.stringify(projectData, null, 2));
      }

      // Cast to bypass TypeScript interface mismatch - backend expects different field names
      const response = await projectService.createProject(projectData as any);

      if (process.env.NODE_ENV === 'development') {
        console.log('Project Creation Response:', response);
      }

      if (response.success) {
        if (formData.manager) {
          notificationService.projectAssigned(formData.manager, formData.name);
        }

        // Reset form
        setFormData({
          name: '',
          projectNumber: '',
          type: '',
          priority: 'medium',
          description: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'US',
          clientName: '',
          clientEmail: '',
          clientPhone: '',
          startDate: '',
          endDate: '',
          totalBudget: '',
          linkedBOQ: '',
          manager: '',
          currency: 'USD'
        });

        setOpen(false);
        onProjectCreated?.(); // Refresh the projects list
      } else {
        console.error('Failed to create project:', response.message);
      }
    } catch (error) {
      console.error('Error creating project:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          New Project
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto modal-content">
        <form onSubmit={handleSubmit}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Create New Project
          </DialogTitle>
          <DialogDescription>
            Enter project details to create a new construction project
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Project Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Project Information</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="project-name">Project Name *</Label>
                <Input
                  id="project-name"
                  placeholder="e.g., Residential Complex Alpha"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-number">Project Number</Label>
                <Input
                  id="project-number"
                  placeholder="e.g., PRJ-2024-001"
                  value={formData.projectNumber}
                  onChange={(e) => setFormData({...formData, projectNumber: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="project-type">Project Type *</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select project type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="industrial">Industrial</SelectItem>
                    <SelectItem value="infrastructure">Infrastructure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="project-description">Description</Label>
              <Textarea
                id="project-description"
                placeholder="Describe the project scope, objectives, and key features..."
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                placeholder="e.g., 123 Main Street"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  placeholder="e.g., New York"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  placeholder="e.g., NY"
                  value={formData.state}
                  onChange={(e) => setFormData({...formData, state: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="zip-code">Zip Code</Label>
                <Input
                  id="zip-code"
                  placeholder="e.g., 10001"
                  value={formData.zipCode}
                  onChange={(e) => setFormData({...formData, zipCode: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Select value={formData.country} onValueChange={(value) => setFormData({...formData, country: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="AF">Afghanistan</SelectItem>
                    <SelectItem value="AL">Albania</SelectItem>
                    <SelectItem value="DZ">Algeria</SelectItem>
                    <SelectItem value="AO">Angola</SelectItem>
                    <SelectItem value="AR">Argentina</SelectItem>
                    <SelectItem value="AM">Armenia</SelectItem>
                    <SelectItem value="AU">Australia</SelectItem>
                    <SelectItem value="AT">Austria</SelectItem>
                    <SelectItem value="AZ">Azerbaijan</SelectItem>
                    <SelectItem value="BH">Bahrain</SelectItem>
                    <SelectItem value="BD">Bangladesh</SelectItem>
                    <SelectItem value="BY">Belarus</SelectItem>
                    <SelectItem value="BE">Belgium</SelectItem>
                    <SelectItem value="BZ">Belize</SelectItem>
                    <SelectItem value="BJ">Benin</SelectItem>
                    <SelectItem value="BO">Bolivia</SelectItem>
                    <SelectItem value="BA">Bosnia and Herzegovina</SelectItem>
                    <SelectItem value="BW">Botswana</SelectItem>
                    <SelectItem value="BR">Brazil</SelectItem>
                    <SelectItem value="BN">Brunei</SelectItem>
                    <SelectItem value="BG">Bulgaria</SelectItem>
                    <SelectItem value="BF">Burkina Faso</SelectItem>
                    <SelectItem value="BI">Burundi</SelectItem>
                    <SelectItem value="KH">Cambodia</SelectItem>
                    <SelectItem value="CM">Cameroon</SelectItem>
                    <SelectItem value="CA">Canada</SelectItem>
                    <SelectItem value="CF">Central African Republic</SelectItem>
                    <SelectItem value="TD">Chad</SelectItem>
                    <SelectItem value="CL">Chile</SelectItem>
                    <SelectItem value="CN">China</SelectItem>
                    <SelectItem value="CO">Colombia</SelectItem>
                    <SelectItem value="CG">Congo</SelectItem>
                    <SelectItem value="CR">Costa Rica</SelectItem>
                    <SelectItem value="HR">Croatia</SelectItem>
                    <SelectItem value="CU">Cuba</SelectItem>
                    <SelectItem value="CY">Cyprus</SelectItem>
                    <SelectItem value="CZ">Czech Republic</SelectItem>
                    <SelectItem value="DK">Denmark</SelectItem>
                    <SelectItem value="DO">Dominican Republic</SelectItem>
                    <SelectItem value="EC">Ecuador</SelectItem>
                    <SelectItem value="EG">Egypt</SelectItem>
                    <SelectItem value="SV">El Salvador</SelectItem>
                    <SelectItem value="EE">Estonia</SelectItem>
                    <SelectItem value="ET">Ethiopia</SelectItem>
                    <SelectItem value="FI">Finland</SelectItem>
                    <SelectItem value="FR">France</SelectItem>
                    <SelectItem value="GA">Gabon</SelectItem>
                    <SelectItem value="GE">Georgia</SelectItem>
                    <SelectItem value="DE">Germany</SelectItem>
                    <SelectItem value="GH">Ghana</SelectItem>
                    <SelectItem value="GR">Greece</SelectItem>
                    <SelectItem value="GT">Guatemala</SelectItem>
                    <SelectItem value="GN">Guinea</SelectItem>
                    <SelectItem value="HN">Honduras</SelectItem>
                    <SelectItem value="HU">Hungary</SelectItem>
                    <SelectItem value="IN">India</SelectItem>
                    <SelectItem value="ID">Indonesia</SelectItem>
                    <SelectItem value="IR">Iran</SelectItem>
                    <SelectItem value="IQ">Iraq</SelectItem>
                    <SelectItem value="IE">Ireland</SelectItem>
                    <SelectItem value="IL">Israel</SelectItem>
                    <SelectItem value="IT">Italy</SelectItem>
                    <SelectItem value="JM">Jamaica</SelectItem>
                    <SelectItem value="JP">Japan</SelectItem>
                    <SelectItem value="JO">Jordan</SelectItem>
                    <SelectItem value="KZ">Kazakhstan</SelectItem>
                    <SelectItem value="KE">Kenya</SelectItem>
                    <SelectItem value="KW">Kuwait</SelectItem>
                    <SelectItem value="KG">Kyrgyzstan</SelectItem>
                    <SelectItem value="LV">Latvia</SelectItem>
                    <SelectItem value="LB">Lebanon</SelectItem>
                    <SelectItem value="LY">Libya</SelectItem>
                    <SelectItem value="LT">Lithuania</SelectItem>
                    <SelectItem value="LU">Luxembourg</SelectItem>
                    <SelectItem value="MK">North Macedonia</SelectItem>
                    <SelectItem value="MG">Madagascar</SelectItem>
                    <SelectItem value="MW">Malawi</SelectItem>
                    <SelectItem value="MY">Malaysia</SelectItem>
                    <SelectItem value="ML">Mali</SelectItem>
                    <SelectItem value="MT">Malta</SelectItem>
                    <SelectItem value="MR">Mauritania</SelectItem>
                    <SelectItem value="MX">Mexico</SelectItem>
                    <SelectItem value="MD">Moldova</SelectItem>
                    <SelectItem value="MN">Mongolia</SelectItem>
                    <SelectItem value="MA">Morocco</SelectItem>
                    <SelectItem value="MZ">Mozambique</SelectItem>
                    <SelectItem value="MM">Myanmar</SelectItem>
                    <SelectItem value="NA">Namibia</SelectItem>
                    <SelectItem value="NP">Nepal</SelectItem>
                    <SelectItem value="NL">Netherlands</SelectItem>
                    <SelectItem value="NZ">New Zealand</SelectItem>
                    <SelectItem value="NI">Nicaragua</SelectItem>
                    <SelectItem value="NE">Niger</SelectItem>
                    <SelectItem value="NG">Nigeria</SelectItem>
                    <SelectItem value="NO">Norway</SelectItem>
                    <SelectItem value="OM">Oman</SelectItem>
                    <SelectItem value="PK">Pakistan</SelectItem>
                    <SelectItem value="PA">Panama</SelectItem>
                    <SelectItem value="PY">Paraguay</SelectItem>
                    <SelectItem value="PE">Peru</SelectItem>
                    <SelectItem value="PH">Philippines</SelectItem>
                    <SelectItem value="PL">Poland</SelectItem>
                    <SelectItem value="PT">Portugal</SelectItem>
                    <SelectItem value="QA">Qatar</SelectItem>
                    <SelectItem value="RO">Romania</SelectItem>
                    <SelectItem value="RU">Russia</SelectItem>
                    <SelectItem value="RW">Rwanda</SelectItem>
                    <SelectItem value="SA">Saudi Arabia</SelectItem>
                    <SelectItem value="SN">Senegal</SelectItem>
                    <SelectItem value="RS">Serbia</SelectItem>
                    <SelectItem value="SL">Sierra Leone</SelectItem>
                    <SelectItem value="SG">Singapore</SelectItem>
                    <SelectItem value="SK">Slovakia</SelectItem>
                    <SelectItem value="SI">Slovenia</SelectItem>
                    <SelectItem value="SO">Somalia</SelectItem>
                    <SelectItem value="ZA">South Africa</SelectItem>
                    <SelectItem value="KR">South Korea</SelectItem>
                    <SelectItem value="SS">South Sudan</SelectItem>
                    <SelectItem value="ES">Spain</SelectItem>
                    <SelectItem value="LK">Sri Lanka</SelectItem>
                    <SelectItem value="SD">Sudan</SelectItem>
                    <SelectItem value="SE">Sweden</SelectItem>
                    <SelectItem value="CH">Switzerland</SelectItem>
                    <SelectItem value="SY">Syria</SelectItem>
                    <SelectItem value="TW">Taiwan</SelectItem>
                    <SelectItem value="TJ">Tajikistan</SelectItem>
                    <SelectItem value="TZ">Tanzania</SelectItem>
                    <SelectItem value="TH">Thailand</SelectItem>
                    <SelectItem value="TG">Togo</SelectItem>
                    <SelectItem value="TT">Trinidad and Tobago</SelectItem>
                    <SelectItem value="TN">Tunisia</SelectItem>
                    <SelectItem value="TR">Turkey</SelectItem>
                    <SelectItem value="TM">Turkmenistan</SelectItem>
                    <SelectItem value="UG">Uganda</SelectItem>
                    <SelectItem value="UA">Ukraine</SelectItem>
                    <SelectItem value="AE">United Arab Emirates</SelectItem>
                    <SelectItem value="GB">United Kingdom</SelectItem>
                    <SelectItem value="US">United States</SelectItem>
                    <SelectItem value="UY">Uruguay</SelectItem>
                    <SelectItem value="UZ">Uzbekistan</SelectItem>
                    <SelectItem value="VE">Venezuela</SelectItem>
                    <SelectItem value="VN">Vietnam</SelectItem>
                    <SelectItem value="YE">Yemen</SelectItem>
                    <SelectItem value="ZM">Zambia</SelectItem>
                    <SelectItem value="ZW">Zimbabwe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Client Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Client Details</h4>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client-name">Client Name *</Label>
                <Input
                  id="client-name"
                  placeholder="e.g., ABC Development Corporation"
                  value={formData.clientName}
                  onChange={(e) => setFormData({...formData, clientName: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="client-email">Client Email</Label>
                  <Input
                    id="client-email"
                    type="email"
                    placeholder="contact@client.com"
                    value={formData.clientEmail}
                    onChange={(e) => setFormData({...formData, clientEmail: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client-phone">Client Phone</Label>
                  <Input
                    id="client-phone"
                    placeholder="+1 (555) 123-4567"
                    value={formData.clientPhone}
                    onChange={(e) => setFormData({...formData, clientPhone: e.target.value})}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Timeline & Budget */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Timeline & Budget</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start-date">Planned Start Date *</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date">Expected End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="total-budget">Total Budget *</Label>
                <Input
                  id="total-budget"
                  type="number"
                  placeholder="0.00"
                  value={formData.totalBudget}
                  onChange={(e) => setFormData({...formData, totalBudget: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Select value={formData.currency} onValueChange={(value) => setFormData({...formData, currency: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD - US Dollar</SelectItem>
                    <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                    <SelectItem value="EUR">EUR - Euro</SelectItem>
                    <SelectItem value="GBP">GBP - British Pound</SelectItem>
                    <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Project Management */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Project Management</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="project-manager">Project Manager *</Label>
                <Select value={formData.manager} onValueChange={(value) => setFormData({...formData, manager: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select project manager" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizationUsers.length === 0 ? (
                      <SelectItem value="loading" disabled>Loading users...</SelectItem>
                    ) : (
                      organizationUsers.filter(user => user.user_id || user.id).map(user => {
                        // Handle both possible ID field names
                        const userId = user.user_id || user.id;
                        const userName = user.full_name || `${user.first_name} ${user.last_name}`;

                        return (
                          <SelectItem key={userId} value={userId}>
                            {userName}
                            {user.job_title && ` - ${user.job_title}`}
                          </SelectItem>
                        );
                      })
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="linked-boq">Link to BOQ *</Label>
                <Select value={formData.linkedBOQ} onValueChange={(value) => setFormData({...formData, linkedBOQ: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select BOQ" />
                  </SelectTrigger>
                  <SelectContent>
                    {boqList.length === 0
                      ? <SelectItem value="_loading" disabled>No BOQs available</SelectItem>
                      : boqList.filter((boq: any) => boq.boq_id || boq.id).map((boq: any) => (
                          <SelectItem key={boq.boq_id || boq.id} value={boq.boq_id || boq.id}>
                            {boq.boq_name || boq.name}
                          </SelectItem>
                        ))
                    }
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading || !formData.name || !formData.type || !formData.linkedBOQ}>
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Creating...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Create Project
              </>
            )}
          </Button>
        </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Archive Project Modal
function ArchiveProjectModal({ project, onProjectUpdated }: { project: any; onProjectUpdated?: () => void }) {
  const [reason, setReason] = useState('');
  const [archiveType, setArchiveType] = useState<'completed' | 'cancelled' | 'postponed' | ''>('');
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const handleArchive = async () => {
    setIsLoading(true);

    try {
      // Update project status to archived/completed/cancelled
      const status = archiveType === 'completed' ? 'completed' : archiveType === 'cancelled' ? 'cancelled' : 'on_hold';
      const response = await projectService.updateProject(project.id, {
        status: status as any
        // notes: reason - not supported by Project interface
      });

      if (response.success) {
        setOpen(false);
        onProjectUpdated?.(); // Refresh the projects list
      } else {
        console.error('Failed to archive project:', response.message);
      }
    } catch (error) {
      console.error('Error archiving project:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
          <Archive className="h-4 w-4 mr-2" />
          Archive Project
        </DropdownMenuItem>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-600">
            <Archive className="h-5 w-5" />
            Archive Project
          </DialogTitle>
          <DialogDescription>
            Archive this project and remove it from active project lists
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-orange-900">Archive Confirmation</h4>
                <p className="text-sm text-orange-800 mt-1">
                  You are about to archive <strong>{project.name}</strong>
                </p>
                <p className="text-sm text-orange-700 mt-1">
                  Progress: {project.progress}% | Budget: {project.budget} | Manager: {project.manager}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Label>Archive Reason</Label>
            <div className="grid grid-cols-1 gap-2">
              <Button
                variant={archiveType === 'completed' ? 'default' : 'outline'}
                className={archiveType === 'completed' ? 'bg-green-600 hover:bg-green-700' : ''}
                onClick={() => setArchiveType('completed')}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Project Completed
              </Button>
              <Button
                variant={archiveType === 'cancelled' ? 'destructive' : 'outline'}
                onClick={() => setArchiveType('cancelled')}
              >
                <X className="h-4 w-4 mr-2" />
                Project Cancelled
              </Button>
              <Button
                variant={archiveType === 'postponed' ? 'secondary' : 'outline'}
                onClick={() => setArchiveType('postponed')}
              >
                <Calendar className="h-4 w-4 mr-2" />
                Project Postponed
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="archive-reason">
              Additional Details {archiveType === 'cancelled' || archiveType === 'postponed' ? '*' : '(Optional)'}
            </Label>
            <Textarea
              id="archive-reason"
              placeholder={
                archiveType === 'completed'
                  ? "Optional: Add completion notes..."
                  : archiveType === 'cancelled'
                  ? "Please provide reason for cancellation..."
                  : archiveType === 'postponed'
                  ? "Please provide reason for postponement..."
                  : "Select archive reason first..."
              }
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={!archiveType}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={archiveType === 'cancelled' ? 'destructive' : 'default'}
            disabled={!archiveType || ((archiveType === 'cancelled' || archiveType === 'postponed') && !reason.trim()) || isLoading}
            onClick={handleArchive}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Archiving...
              </>
            ) : (
              <>
                <Archive className="h-4 w-4 mr-2" />
                Archive Project
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Delete Project Modal Component
function DeleteProjectModal({ project, onProjectDeleted }: { project: any; onProjectDeleted?: () => void }) {
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const handleDelete = async () => {
    setIsLoading(true);

    try {
      const response = await projectService.deleteProject(project.id);

      if (response.success) {
        setOpen(false);
        onProjectDeleted?.(); // Refresh the projects list
      } else {
        console.error('Failed to delete project:', response.message);
      }
    } catch (error) {
      console.error('Error deleting project:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-red-600">
          <X className="h-4 w-4 mr-2" />
          Delete Project
        </DropdownMenuItem>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Delete Project
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete "{project.name}"? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-red-800">This will permanently delete:</h4>
                <ul className="text-sm text-red-700 mt-2 space-y-1">
                  <li>• All project data and settings</li>
                  <li>• Associated schedules and tasks</li>
                  <li>• Daily logs and documents</li>
                  <li>• Project history and reports</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Deleting...
              </>
            ) : (
              <>
                <X className="h-4 w-4 mr-2" />
                Delete Project
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProjectViewModal({ project }: { project: any }) {
  const [open, setOpen] = useState(false);
  const { orgId } = useAuth();
  const { data: boqList = [] } = useBoqs(open ? orgId : null);

  const resolveBoqName = (id: string | null | undefined) => {
    if (!id) return '—';
    const match = boqList.find((b: any) => (b.boq_id || b.id) === id);
    return match ? (match.boq_name || match.name) : id;
  };

  const field = (label: string, value: string | number | null | undefined) => (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <p className="text-sm font-medium">{value || '—'}</p>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
          <Eye className="h-4 w-4 mr-2" />
          View Details
        </DropdownMenuItem>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Project Details
          </DialogTitle>
          <DialogDescription>Complete information for {project.name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Project Information</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {field('Project Name', project.name)}
              {field('Project Number', project.projectNumber)}
              {field('Type', project.category)}
              {field('Status', project.status)}
              {field('Priority', project.priority)}
              {field('Progress', project.progress != null ? `${project.progress}%` : undefined)}
              {field('Linked BOQ', resolveBoqName(project.linkedBOQId))}
            </div>
            {project.description && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Description</Label>
                <p className="text-sm">{project.description}</p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Location</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {field('Address', project.address)}
              {field('City', project.city)}
              {field('State', project.state)}
              {field('Zip Code', project.zipCode)}
              {field('Country', project.country ? (COUNTRY_NAMES[project.country] || project.country) : '—')}
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Client & Management</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {field('Client Name', project.client)}
              {field('Client Email', project.clientEmail)}
              {field('Client Phone', project.clientPhone)}
              {field('Project Manager', project.manager)}
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Timeline & Budget</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {field('Start Date', project.startDate)}
              {field('End Date', project.endDate)}
              {field('Total Budget', project.budget)}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProjectEditModal({ project, onProjectUpdated }: { project: any; onProjectUpdated?: () => void }) {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { orgId } = useAuth();
  const { data: organizationUsers = [] } = useOrgUsers(open ? orgId : null);
  const { data: boqList = [] } = useBoqs(open ? orgId : null);
  const [formData, setFormData] = useState({
    name: project.name || '',
    projectNumber: project.projectNumber || '',
    type: project.category?.toLowerCase() || '',
    priority: project.priority?.toLowerCase() || 'medium',
    description: project.description || '',
    address: project.address || '',
    city: project.city || '',
    state: project.state || '',
    zipCode: project.zipCode || '',
    country: project.country || 'US',
    clientName: project.client || '',
    clientEmail: project.clientEmail || '',
    clientPhone: project.clientPhone || '',
    startDate: project.startDate && project.startDate !== 'N/A' ? project.startDate : '',
    endDate: project.endDate && project.endDate !== 'N/A' ? project.endDate : '',
    totalBudget: project.totalBudget?.toString() || '',
    manager: project.managerId || '',
    linkedBOQ: project.linkedBOQId || '',
  });


  const handleSave = async () => {
    setIsLoading(true);
    try {
      const response = await projectService.updateProject(project.id, {
        project_name: formData.name,
        project_number: formData.projectNumber || null,
        project_type: formData.type,
        priority: formData.priority,
        description: formData.description || null,
        address: formData.address || null,
        city: formData.city || null,
        state: formData.state || null,
        zip_code: formData.zipCode || null,
        country: formData.country || 'US',
        project_manager_id: formData.manager || null,
        client_name: formData.clientName || null,
        client_email: formData.clientEmail || null,
        client_phone: formData.clientPhone || null,
        start_date: formData.startDate || null,
        end_date: formData.endDate || null,
        total_budget: formData.totalBudget ? parseFloat(formData.totalBudget) : null,
        linked_boq_id: formData.linkedBOQ || null,
      } as any);
      if (response.success) {
        setOpen(false);
        onProjectUpdated?.();
      }
    } catch (error) {
      console.error('Error updating project:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
          <Edit className="h-4 w-4 mr-2" />
          Edit Project
        </DropdownMenuItem>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Edit Project
          </DialogTitle>
          <DialogDescription>Update project information for {project.name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Project Information</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Project Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Project Number</Label>
                <Input value={formData.projectNumber} onChange={(e) => setFormData({...formData, projectNumber: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({...formData, type: v})}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="industrial">Industrial</SelectItem>
                    <SelectItem value="infrastructure">Infrastructure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Location</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Address</Label>
                <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>City</Label>
                <Input value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Zip Code</Label>
                <Input value={formData.zipCode} onChange={(e) => setFormData({...formData, zipCode: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <Select value={formData.country} onValueChange={(v) => setFormData({...formData, country: v})}>
                  <SelectTrigger><SelectValue placeholder="Select country" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {Object.entries(COUNTRY_NAMES).map(([code, name]) => (
                      <SelectItem key={code} value={code}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Client Details</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Client Name</Label>
                <Input value={formData.clientName} onChange={(e) => setFormData({...formData, clientName: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Client Email</Label>
                <Input type="email" value={formData.clientEmail} onChange={(e) => setFormData({...formData, clientEmail: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Client Phone</Label>
                <Input value={formData.clientPhone} onChange={(e) => setFormData({...formData, clientPhone: e.target.value})} />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Timeline & Budget</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={formData.startDate} onChange={(e) => setFormData({...formData, startDate: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input type="date" value={formData.endDate} onChange={(e) => setFormData({...formData, endDate: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Total Budget</Label>
                <Input type="number" value={formData.totalBudget} onChange={(e) => setFormData({...formData, totalBudget: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Project Manager</Label>
                <Select value={formData.manager} onValueChange={(v) => setFormData({...formData, manager: v})}>
                  <SelectTrigger><SelectValue placeholder="Select manager" /></SelectTrigger>
                  <SelectContent>
                    {organizationUsers.filter((u: any) => u.user_id || u.id).map((u: any) => {
                      const uid = u.user_id || u.id;
                      const uname = u.full_name || `${u.first_name} ${u.last_name}`;
                      return <SelectItem key={uid} value={uid}>{uname}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Link to BOQ *</Label>
                <Select value={formData.linkedBOQ} onValueChange={(v) => setFormData({...formData, linkedBOQ: v})}>
                  <SelectTrigger><SelectValue placeholder="Select BOQ" /></SelectTrigger>
                  <SelectContent>
                    {boqList.length === 0
                      ? <SelectItem value="_loading" disabled>No BOQs available</SelectItem>
                      : boqList.filter((boq: any) => boq.boq_id || boq.id).map((boq: any) => (
                          <SelectItem key={boq.boq_id || boq.id} value={boq.boq_id || boq.id}>
                            {boq.boq_name || boq.name}
                          </SelectItem>
                        ))
                    }
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isLoading}>Cancel</Button>
          <Button onClick={handleSave} disabled={isLoading || !formData.name || !formData.type || !formData.linkedBOQ}>
            {isLoading ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Saving...</>
            ) : (
              <><Save className="h-4 w-4 mr-2" />Save Changes</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Project Approval Modal
function ProjectApprovalModal({ project, onProjectUpdated }: { project: any; onProjectUpdated?: () => void }) {
  const [open, setOpen] = useState(false);
  const [decision, setDecision] = useState<'approve' | 'reject' | ''>('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!decision) return;
    setSaving(true);
    try {
      const newStatus = decision === 'approve' ? 'active' : 'cancelled';
      await httpClient.patch(`/api/v1/projects/${project.id}`, { status: newStatus });
      setOpen(false);
      setDecision('');
      setReason('');
      onProjectUpdated?.();
    } catch (err: any) {
      console.error('Failed to update project status:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
          <CheckCircle className="h-4 w-4 mr-2" />
          Approve Project
        </DropdownMenuItem>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Project Approval
          </DialogTitle>
          <DialogDescription>
            Review and approve or reject this project
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium">{project.name}</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Manager: {project.manager}
            </p>
            <p className="text-sm text-muted-foreground">
              Budget: {project.budget} | Progress: {project.progress}%
            </p>
          </div>

          <div className="space-y-3">
            <Label>Decision</Label>
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant={decision === 'approve' ? 'default' : 'outline'}
                className={decision === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                onClick={() => setDecision('approve')}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve
              </Button>
              <Button
                variant={decision === 'reject' ? 'destructive' : 'outline'}
                onClick={() => setDecision('reject')}
              >
                <X className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="approval-reason">
              {decision === 'approve' ? 'Approval' : decision === 'reject' ? 'Rejection' : ''} Reason
            </Label>
            <Textarea
              id="approval-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                decision === 'approve'
                  ? "Optional: Add approval comments..."
                  : decision === 'reject'
                  ? "Please provide reason for rejection..."
                  : "Select approve or reject first..."
              }
              rows={3}
              disabled={!decision}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button disabled={!decision || saving} onClick={handleSubmit}>
            {saving ? 'Submitting...' : `Submit ${decision === 'approve' ? 'Approval' : decision === 'reject' ? 'Rejection' : 'Decision'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Project Actions Menu Component
function ProjectActionsMenu({ project, onProjectUpdated }: { project: any; onProjectUpdated?: () => void }) {
  const { canWrite, canDelete } = usePermission();
  const { navigate } = useAppNav();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={() => navigate('/projects/detail', { projectId: project.id })}>
          <Eye className="h-4 w-4 mr-2" />View Project
        </DropdownMenuItem>
        {canWrite('/projects') && <ProjectEditModal project={project} onProjectUpdated={onProjectUpdated} />}
        <ProjectApprovalModal project={project} onProjectUpdated={onProjectUpdated} />
        <ArchiveProjectModal project={project} onProjectUpdated={onProjectUpdated} />
        {canDelete('/projects') && <DeleteProjectModal project={project} onProjectDeleted={onProjectUpdated} />}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AllProjects() {
  const { orgId } = useAuth();
  const orgCurrency = useOrgCurrency();
  const { navigate } = useAppNav();
  const { data: rawProjects = [], isLoading: loading, error: projectsError, refetch } = useProjects(orgId);
  const { data: rawUsers = [] } = useOrgUsers(orgId);

  // Build user ID → name map from cached users query
  const usersMap = useMemo(() => {
    const map: Record<string, string> = {};
    rawUsers.forEach((u: any) => {
      const uid = u.user_id || u.id;
      if (uid) map[uid] = u.full_name || `${u.first_name} ${u.last_name}`;
    });
    return map;
  }, [rawUsers]);

  // Transform raw API projects into the shape used by the table and modals
  const projects = useMemo(() => rawProjects.map((project: any) => {
    const managerId = project.project_manager_id;
    return {
      id: project.project_id,
      name: project.project_name,
      projectNumber: project.project_number,
      status: project.status,
      priority: project.priority,
      progress: project.completion_percentage || 0,
      manager: managerId ? (usersMap[managerId] || 'Unknown') : 'Unassigned',
      managerId,
      startDate: project.start_date || 'N/A',
      endDate: project.end_date || 'N/A',
      budget: project.total_budget ? formatCurrencyCompact(project.total_budget, orgCurrency) : 'N/A',
      totalBudget: project.total_budget,
      category: project.project_type || 'General',
      description: project.description,
      address: project.address,
      city: project.city,
      state: project.state,
      zipCode: project.zip_code,
      country: project.country,
      location: `${project.city || ''} ${project.state || ''}`.trim() || 'N/A',
      client: project.client_name || 'Unknown',
      clientEmail: project.client_email,
      clientPhone: project.client_phone,
      linkedBOQId: project.linked_boq_id || null,
    };
  }), [rawProjects, usersMap, orgCurrency]);

  const error = projectsError ? (projectsError as Error).message || 'Failed to load projects' : '';

  const handleProjectCreated = () => {
    refetch();
  };

  // Calculate stats from projects data
  const totalProjects = projects.length;
  const activeProjects = projects.filter((p: any) => p.status?.toLowerCase() === 'active').length;
  const completedProjects = projects.filter((p: any) => p.status?.toLowerCase() === 'completed').length;
  const totalBudget = projects.reduce((sum: number, p: any) => {
    const budget = parseFloat(p.totalBudget) || 0;
    return sum + budget;
  }, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">All Projects</h2>
          <p className="text-muted-foreground">Manage and monitor all construction projects</p>
        </div>
        <NewProjectModal onProjectCreated={handleProjectCreated} />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Total Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{totalProjects}</div>
            <p className="text-xs text-muted-foreground">across all statuses</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Active Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">{activeProjects}</div>
            <p className="text-xs text-muted-foreground">currently in progress</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Completed Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{completedProjects}</div>
            <p className="text-xs text-muted-foreground">successfully delivered</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Total Budget</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-700">{formatCurrencyCompact(totalBudget, orgCurrency)}</div>
            <p className="text-xs text-muted-foreground">across all projects</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search projects..." className="pl-9" />
        </div>
        <AdvancedFilterPopover />
      </div>

      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading projects...</p>
        </div>
      )}

      {error && (
        <div className="text-center py-12">
          <AlertTriangle className="mx-auto h-12 w-12 text-destructive mb-4" />
          <h3 className="text-lg font-medium mb-2">Failed to load projects</h3>
          <p className="text-muted-foreground">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="grid gap-4">
          {projects.map((project: any) => (
          <Card key={project.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/projects/detail', { projectId: project.id })}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold">{project.name}</h3>
                    <Badge
                      variant={
                        project.status?.toLowerCase() === "active" ? "default" :
                        project.status?.toLowerCase() === "completed" ? "default" :
                        project.status?.toLowerCase() === "delayed" ? "destructive" :
                        project.status?.toLowerCase() === "on-hold" || project.status?.toLowerCase() === "on_hold" ? "secondary" :
                        project.status?.toLowerCase() === "cancelled" ? "destructive" :
                        "outline"
                      }
                      className={
                        project.status?.toLowerCase() === "active" ? "bg-blue-600" :
                        project.status?.toLowerCase() === "completed" ? "bg-green-600" :
                        project.status?.toLowerCase() === "planning" ? "bg-purple-600 text-white" :
                        project.status?.toLowerCase() === "on-hold" || project.status?.toLowerCase() === "on_hold" ? "bg-yellow-100 text-yellow-800" :
                        project.status?.toLowerCase() === "delayed" ? "" :
                        project.status?.toLowerCase() === "cancelled" ? "" :
                        ""
                      }
                    >
                      {project.status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Manager:</span>
                      <p className="font-medium">{project.manager}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Planned Start Date:</span>
                      <p className="font-medium">{project.startDate}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Budget:</span>
                      <p className="font-medium">{project.totalBudget ? formatCurrencyCompact(parseFloat(project.totalBudget), orgCurrency) : 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Progress:</span>
                      <p className="font-medium">{project.progress}%</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Category:</span>
                      <p className="font-medium">{project.category}</p>
                    </div>
                  </div>
                </div>
                <div onClick={e => e.stopPropagation()}>
                  <ProjectActionsMenu project={project} onProjectUpdated={handleProjectCreated} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        </div>
      )}
    </div>
  );
}
